import { ReactNode } from "react";
import { ChevronLeft } from "lucide-react";
import { useT } from "@/lib/i18n";
import { GUTTER } from "./layout";
import { NOISE_BACKGROUND, softGlow } from "./glow";

/**
 * The wordmark, shown once at full size on the opening screen and thereafter
 * as the wordmark alone in the top bar.
 *
 * The word used to *fly* there on a shared `layoutId`. That is a framer layout
 * animation: it measures both boxes and drives a transform every frame until
 * it settles, and on the screen straight after the intro — the only transition
 * that runs one — it was the prime suspect for a phone that stayed janky the
 * whole time it sat there. Instrumented in the browser it was still carrying a
 * mid-flight transform a second and a half after the swap.
 *
 * So the two copies simply cross over: the intro's lockup goes with its
 * screen, the top bar's fades in. No measurement, no per-frame work. If the
 * device turns out not to care, the flight is a `layoutId` on both images and
 * a `transition` away — it is deliberately still one edit, not a rewrite.
 */
const BRAND_MARK_SRC = "/logos/outclose.png";
const BRAND_WORDMARK_SRC = "/logos/outclose_horizontal.png";


interface OnboardingShellProps {
  /** 0–100. Rendered as a thin unlabeled line: no counters, per the design. */
  progress: number;
  showProgress?: boolean;
  canGoBack: boolean;
  onBack?: () => void;
  /** Ambient glow behind the content. Each screen picks its own hue. */
  glow?: "primary" | "secondary" | "accent" | "none";
  /**
   * Full-bleed treatment behind everything. `grid` is the city handoff's
   * backdrop from the design: a wide warm bloom plus a faint skewed grid that
   * reads as a map seen at an angle.
   */
  backdrop?: "none" | "grid";
  glowPosition?: "top-right" | "top-left" | "bottom-left" | "bottom-right" | "center";
  continueLabel?: string;
  canContinue?: boolean;
  onContinue?: () => void;
  /** Hides the button without removing it from the layout. */
  hideContinue?: boolean;
  /** Line above the button — sign-in link, tap hint, reassurance. */
  dockNote?: ReactNode;
  /** Changing this remounts the note; keep it stable to leave it alone. */
  dockNoteKey?: string;
  /** The screen's identity. Changing it remounts the body. */
  stepKey: string | number;
  children: ReactNode;
}

/**
 * The ambient glow, drawn rather than blurred. See `glow.ts` for why, and for
 * the shape of the falloff.
 */
const GLOW_GRADIENT = {
  primary: softGlow("239,98,37", 0.22),
  secondary: softGlow("184,104,192", 0.2),
  accent: softGlow("54,162,173", 0.18),
} as const;

/**
 * Where the glow sits, and how big it is.
 *
 * It grows with the window rather than staying the 440px that suits a phone.
 * At a fixed size on a desktop it stops reading as light on the page and
 * starts reading as an object on it: a distinct disc parked in the corner,
 * with its own visible extent, next to a 480px column. Scaled up it goes back
 * to being a wash coming in from off-screen, and the corner offsets grow with
 * it so the same amount of it stays off the edge.
 */
const GLOW_SIZE = "w-[440px] h-[440px] sm:w-[820px] sm:h-[820px]";

/**
 * The content column: uncapped below `sm`, capped above it.
 *
 * The cap keeps the flow's phone shape in a desktop window rather than
 * letterboxing a phone — and plenty of Android devices report a CSS viewport
 * wider than 480 (a 1080px screen at dpr 2 is 540). On those, a fixed cap left
 * ~30px of background down each side, and anything full-bleed stopped short of
 * the screen: the story wheel and the print conveyor both bleed to this
 * column, so a capped column is as far as they can reach.
 *
 * Applied to each band's *contents* rather than wrapped around the three of
 * them, which is what lets the sticky bars paint their background the whole
 * way across while their contents stay in line with the body.
 */
const COLUMN = "w-full sm:max-w-[480px] mx-auto";

const GLOW_POSITION = {
  "top-right": "-top-24 -right-24 sm:-top-64 sm:-right-64",
  "top-left": "-top-24 -left-24 sm:-top-64 sm:-left-64",
  "bottom-left": "bottom-24 -left-28 sm:-bottom-56 sm:-left-72",
  "bottom-right": "-bottom-24 -right-20 sm:-bottom-64 sm:-right-56",
  center: "top-1/4 left-1/2 -translate-x-1/2 sm:top-0",
} as const;

/**
 * Chrome for every onboarding screen: the drifting glow, the thin progress
 * line, the back chevron, and the sticky CTA dock.
 *
 * Deliberately thinner than the legacy `StepShell` — the design drops section
 * pills and step counters ("3 of 17" reads as work), leaving a single
 * unlabeled hairline as the only progress signal. Handoff and account screens
 * hide the chrome entirely by passing `showProgress={false}`.
 */
export default function OnboardingShell({
  progress,
  showProgress = true,
  canGoBack,
  onBack,
  glow = "primary",
  glowPosition = "top-right",
  backdrop = "none",
  continueLabel,
  canContinue = true,
  onContinue,
  hideContinue = false,
  dockNote,
  dockNoteKey,
  stepKey,
  children,
}: OnboardingShellProps) {
  const { t } = useT();
  // The opening screen owns the wordmark; every screen after it borrows the
  // mark in the top bar. `stepKey` is the screen id, so this is the intro.
  const onIntro = stepKey === "intro";
  const label = continueLabel ?? t("onboarding.continue");
  const hasDockContent = Boolean(dockNote) || !hideContinue;

  return (
    // Opaque canvas, in whichever theme the user chose.
    //
    // This used to force `dark` on the root, on the reasoning that the design
    // has one palette. That is a reason to *draw* it darkly, not to override a
    // preference the user set: someone who picked light got light everywhere
    // in the app and then a black screen for signup. The screens were already
    // written with `dark:` variants throughout, so honouring the setting costs
    // nothing but this class and the two decorations below that assumed a dark
    // ground.
    // No `overflow-hidden` on this root, deliberately: an ancestor that clips
    // becomes the dock's scroll container, and a sticky element inside a box
    // that doesn't scroll simply doesn't stick. The decorations that needed
    // the clipping carry it themselves — each is `absolute inset-0
    // overflow-hidden` in its own layer.
    <div className="min-h-viewport w-full flex flex-col relative bg-white dark:bg-[#0C0B10]">
      {/* The city handoff's backdrop. Both layers are full-bleed and sit
          behind the column, which is why they live in the shell rather than
          the screen — inside the body they'd be clipped by its padding and
          would scroll with the content. Values are the design's own. */}
      {backdrop === "grid" && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
          {/* Drawn rather than blurred, and scaled with the window, for the
              same reasons as the corner glow above. */}
          <div
            className="absolute top-[110px] sm:top-[40px] left-1/2 -translate-x-1/2 w-[440px] h-[440px] sm:w-[760px] sm:h-[760px] rounded-full"
            style={{ background: softGlow("239,98,37", 0.2) }}
          />
          {/* Neither set of lines is truly parallel — the slight skew is what
              makes it read as ground receding rather than graph paper. */}
          {/* `currentColor` so the receding grid can be dark lines on light
              and light lines on dark — as white-on-white it simply vanished. */}
          <svg
            viewBox="0 0 390 420"
            className="absolute top-[60px] left-0 w-full opacity-50 text-black/[0.10] dark:text-white/[0.08]"
          >
            {[
              "M-20 90 L410 130",
              "M-20 180 L410 200",
              "M-20 300 L410 280",
              "M80 -20 L60 440",
              "M200 -20 L215 440",
              "M320 -20 L300 440",
            ].map((d) => (
              <path key={d} d={d} stroke="currentColor" strokeWidth="1" />
            ))}
          </svg>
        </div>
      )}

      {/* Ambient glow — purely decorative, sits behind everything.

          Static. It used to be keyed on its own colour and corner, so every
          screen change tore it down and faded a fresh one in over 600ms: a
          320px box under a 64px blur, re-rasterized and re-composited for the
          length of the fade, on top of whatever the new screen was doing. It
          moves corners between steps either way; doing that in one repaint
          instead of over half a second is the whole difference. */}
      {glow !== "none" && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
        {/* Wider than the 320px it was: the blur used to spread it well past
            its own box, and the gradient has to cover that ground itself. */}
        <div
          aria-hidden
          className={`pointer-events-none absolute rounded-full ${GLOW_SIZE} ${GLOW_POSITION[glowPosition]}`}
          style={{ background: GLOW_GRADIENT[glow] }}
        />
        {/* The dither. `sm` and up only: banding is a function of how far the
            ramp is stretched, so it is a desktop problem, and this is one more
            full-screen layer to composite on the device that can least afford
            it. */}
        <div
          aria-hidden
          className="hidden sm:block absolute inset-0 opacity-[0.035] mix-blend-overlay"
          style={{ backgroundImage: NOISE_BACKGROUND }}
        />
        </div>
      )}

      {/* Top bar — back chevron and the hairline share a row, as designed.
          Sticky and opaque for the same reason as the dock: the page is the
          scroller now, so otherwise the way back and the sense of progress
          scroll away on exactly the screens long enough to need scrolling.
          `z-10` because it comes before the content in the DOM and would be
          painted over by it.

          The bar spans the window and only its *contents* are held to the
          column. Capped, its opaque background ended mid-screen on a desktop
          and left two vertical seams with the glow showing through either
          side, which reads as a panel floating on the page rather than as the
          top of it. */}
      <div
        className="sticky top-0 z-10 top-safe-padding-4 pb-1 bg-white dark:bg-[#0C0B10]"
      >
        <div className={`${COLUMN} ${GUTTER} flex items-center gap-3`}>
          <button
            onClick={onBack}
            disabled={!canGoBack}
            className={`w-8 h-8 -ml-2 flex items-center justify-center rounded-full transition-all ${
              canGoBack
                ? "opacity-100 hover:bg-black/5 dark:hover:bg-white/10"
                : "opacity-0 pointer-events-none"
            }`}
            aria-label={t("onboarding.back")}
          >
            <ChevronLeft className="w-5 h-5 text-neutral-900 dark:text-neutral-100" />
          </button>
          <div
            className={`flex-1 h-[2px] rounded-full overflow-hidden bg-black/10 dark:bg-white/[0.14] transition-opacity duration-300 ${
              showProgress ? "opacity-100" : "opacity-0"
            }`}
            role={showProgress ? "progressbar" : undefined}
            aria-valuenow={showProgress ? Math.round(progress) : undefined}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={showProgress ? t("onboarding.progressLabel") : undefined}
          >
            {/* The one thing on a screen change that still moves, because it
                is the only progress signal in the flow and a bar that jumps
                doesn't read as progress. A CSS transform transition rather
                than an animated `width`: same 400ms, but it runs off the main
                thread and never asks for layout, where animating the width
                relaid the bar on every frame of every step. */}
            <div
              className="h-full w-full origin-left rounded-full bg-primary-500 transition-transform duration-[400ms] ease-out motion-reduce:transition-none"
              style={{
                transform: `scaleX(${Math.max(2, Math.min(100, progress)) / 100})`,
              }}
            />
          </div>

          {!onIntro && (
            <img
              src={BRAND_WORDMARK_SRC}
              alt="Outclose"
              className="onboarding-brand-in w-auto flex-none dark:invert"
              style={{ height: 13 }}
            />
          )}
        </div>
      </div>

        {/* Body. `min-h-0` keeps the flex child from growing past the viewport
            so overflow scrolls inside this box rather than the document. */}
        {/* `overflow-x-clip`, not the `auto` that `overflow-y-auto` alone
            implies: a decorative element that reaches past the column — the
            welcome's bloom, a card mid-spring — would otherwise hand the whole
            flow a horizontal scrollbar. Clipping keeps the vertical scroll and
            refuses the horizontal one, which is what every full-bleed piece in
            here already assumes. */}
        {/* No scroller here — the page is the scroller, and the only one.
            Two of them stacked is what iOS reads as a double scroll: the inner
            box swallows the swipe even with nothing to scroll, so the document
            never moves and the toolbars never retract. A screen with more
            content than fits makes the page longer instead. */}
      <div
        className={`relative flex-1 flex flex-col ${COLUMN} ${GUTTER} pb-2 overflow-x-clip`}
      >
          {onIntro && (
            <div className="flex items-center gap-2 self-start flex-none">
              {/* Decorative: the word beside it carries the name, and two
                  images both announcing "Outclose" is one too many. */}
              <img
                src={BRAND_MARK_SRC}
                alt=""
                aria-hidden
                className="w-auto dark:invert"
                style={{ height: 26 }}
              />
              {/* Optically smaller than the mark — the word's cap height fills
                  its box, the mark's disc does not, so matching the boxes
                  makes the word look oversized. */}
              <img
                src={BRAND_WORDMARK_SRC}
                alt="Outclose"
                className="w-auto dark:invert"
                style={{ height: 20 }}
              />
            </div>
          )}
          {/* Screens swap outright — no cross-fade between them.

              There used to be an `AnimatePresence mode="wait"` here, fading
              the old screen out over 200ms and the new one in after it. On a
              modern phone that is nothing; on an iPhone X it is the worst
              possible 400ms, because `mode="wait"` keeps *both* screens
              mounted and the outgoing one keeps running whatever it was doing
              while the incoming one mounts, measures and starts its own
              entrance. Every screen change paid for two screens at once, which
              is why the flow felt heavy everywhere rather than on one screen.

              The screens each open with their own motion, so the transition
              was never carrying the flow's character — it was a fade on top of
              animations that already existed. `key` still forces a remount, so
              those entrances restart per screen exactly as before. */}
          <div key={stepKey} className="flex-1 flex flex-col min-h-0">
            {children}
          </div>
      </div>

        {/* The dock — one shared bottom slot for the whole flow. Screens
            describe its contents (see DockSpec); none of them render a button
            of their own. Two buttons in the tree, even for one frame, is the
            flicker this avoids.

            It sizes to what's actually in it: a screen with no note and no
            button leaves nothing but the bottom safe area, handing the space
            back to the body. That's safe for the button specifically because
            the dock is bottom-anchored and the button is its last child — its
            position is measured up from the bottom edge, so whether a note
            sits above it changes only where the dock's *top* is. The button
            itself lands in the same place on every screen that shows one.

            The safe-area padding stays unconditionally: it's what keeps
            content off the iPhone home indicator, empty dock or not. */}
        {/* Opaque, in the shell's own canvas colour: the page scrolls behind
            this now, and a transparent dock let content run under the button
            on its way past. */}
      <div
        className={`sticky left-0 right-0 bottom-0 z-10 mt-auto bottom-safe-padding-6 bg-white dark:bg-[#0C0B10] ${
          hasDockContent ? "pt-3" : ""
        }`}
      >
        <div className={`${COLUMN} ${GUTTER}`}>
          {/* Swaps with the screen, for the same reason it does. `mode="wait"`
              here meant the new note waited out the old one's fade, so with
              the screen itself now switching instantly the note would have
              arrived 180ms late on every step — the one piece of the old
              chrome still visibly lagging. */}
          {dockNote && (
            <div
              key={dockNoteKey}
              className={`flex items-center justify-center text-center text-[13px] text-neutral-500 dark:text-neutral-400 ${
                hideContinue ? "" : "mb-4"
              }`}
            >
              {dockNote}
            </div>
          )}

          {!hideContinue && (
            <button
              onClick={onContinue}
              disabled={!canContinue}
              // Border and font weight are constant across both states so
              // enabling the button doesn't nudge its label by a pixel.
              // ~50px tall, matching the design. It was 58px: `text-base`
            // carries a 24px line-height, where the design's button sits on a
            // normal (~19px) one, so the label had 5px of dead space around it
            // before any padding. Trimming the leading and a little padding
            // brings it in line with the 52px option rows without going near
            // the 44px minimum touch target.
            className={`w-full py-3.5 leading-tight rounded-full text-base font-semibold border transition-colors ${
                canContinue
                  ? "bg-primary-500 border-primary-500 text-white active:scale-[0.99] hover:bg-primary-600 hover:border-primary-600"
                  : "text-neutral-400 dark:text-neutral-500 cursor-not-allowed bg-black/5 dark:bg-white/5 border-black/10 dark:border-white/10"
              }`}
            >
              {label}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
